import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

import CardDialog from '~/components/CardDialog';
import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import EmptyState from '~/components/core/EmptyState';
import InnerTabs from '~/components/core/Layout/InnerTabs/InnerTabs';
import { PERMISSION_ACTIONS, PERMISSION_VERBS } from '~/components/core/Permissions/PermissionUtils';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import { useHasPermission } from '~/components/hooks/useHasPermission';
import { NewRefreshIcon, UploadIcon } from '~/components/icons';
import useOrganization from '~/components/OrganizationContext';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import type { OrganizationModel } from '~/components/types/organization-types';
import useDataFetcher from '~/components/useDataFetcher';
import type {
  IncomingPromotionListResult,
  OutgoingPromotionListResult,
  PairingListResult,
} from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

import CreatePromotionDialog from './CreatePromotionDialog';
import CreateUploadPromotionDialog from './CreateUploadPromotionDialog';
import IncomingPromotionDetailDialog from './IncomingPromotionDetailDialog';
import OutgoingPromotionDetailDialog from './OutgoingPromotionDetailDialog';
import type { PromotionsPaneProps } from './PromotionsPanes';
import { IncomingPromotionsPane, OutgoingPromotionsPane } from './PromotionsPanes';
import { isTargetUnreachableError, promotionsBaseRoute, TARGET_UNREACHABLE_MESSAGE } from './promotionsUtils';
import RollbackPromotionDialog from './RollbackPromotionDialog';
import type { IncomingPromotionRow } from './types';
import { PROMOTION_STATUS } from './types';

// Representative column count for the transient full-tab skeleton shown before we know which table(s)
// will render. The real tables derive their skeleton width from `columns.length` (outgoing 6–7 with the
// optional Target-env column, incoming 7); 6 keeps this placeholder aligned rather than under-drawn.
const FULL_TAB_SKELETON_COLUMNS = 6;

// Sub-tab route segments under .../promotions. Incoming leads: reviewing is the actionable flow, and the
// "Configuration locked" banner above the tabs refers to it.
const PROMOTION_TABS = { INCOMING: 'incoming', OUTGOING: 'outgoing' } as const;

interface PromotionsTabProps {
  /** Super-org (internal admin) context. Gates the internal-only "Promote from file" upload entry point,
   * mirroring the Snapshots & Backup `showSnapshots = isInternal` precedent. The live "+ New promotion"
   * flow stays available externally as before. */
  isInternal?: boolean;
}

const PromotionsTab: React.FC<PromotionsTabProps> = ({ isInternal = false }) => {
  const { organization, reloadOrganization } = useSysconfig() as {
    organization: OrganizationModel;
    reloadOrganization: () => void;
  };
  // Several org-scoped configs an apply can mutate (contact roles, notification/communication categories,
  // communication config, Claim Bar/Sidebar & stage config) live on OrganizationContext, not the org
  // model — so refresh them alongside the org model when a promotion is applied.
  const { reloadOrganizationContext } = useOrganization() as {
    reloadOrganizationContext?: () => Promise<unknown>;
  };
  const baseRoute = promotionsBaseRoute(organization.id);
  const canWrite = useHasPermission({
    action: PERMISSION_ACTIONS.SYSCONFIG_CONFIG_MANAGEMENT,
    verb: PERMISSION_VERBS.WRITE,
  });

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [outgoingDetailUuid, setOutgoingDetailUuid] = useState<string | null>(null);
  const [incomingDetailUuid, setIncomingDetailUuid] = useState<string | null>(null);
  const [rollbackRow, setRollbackRow] = useState<IncomingPromotionRow | null>(null);
  // A cross-deployment target was unreachable during the on-load refresh (502). Non-blocking + retryable.
  const [refreshUnreachable, setRefreshUnreachable] = useState(false);
  // In-flight guard so the Retry button can't fire concurrent refreshes.
  const [isRefreshing, setIsRefreshing] = useState(false);

  const {
    isLoading: outgoingLoading,
    isError: outgoingError,
    data: outgoing,
    reloadData: reloadOutgoing,
  } = useDataFetcher<OutgoingPromotionListResult>(`${baseRoute}/outgoing`);
  const {
    isLoading: incomingLoading,
    isError: incomingError,
    data: incoming,
    reloadData: reloadIncoming,
  } = useDataFetcher<IncomingPromotionListResult>(`${baseRoute}/incoming`);
  const { isLoading: pairingsLoading, data: pairings } = useDataFetcher<PairingListResult>(`${baseRoute}/pairings`);

  const reloadAll = useCallback(async () => {
    await Promise.all([reloadOutgoing(), reloadIncoming()]);
  }, [reloadOutgoing, reloadIncoming]);

  // An apply mutates target-org config on two surfaces: the org model (feature/config flags) and the
  // org-scoped fetchers on OrganizationContext. Refresh both so the applied changes show without a manual
  // page reload. reloadOrganizationContext is a background allSettled refresh, so its result is ignored.
  const handleApplied = useCallback(() => {
    reloadOrganization();
    void reloadOrganizationContext?.();
  }, [reloadOrganization, reloadOrganizationContext]);

  // Reconcile the source-side outgoing cache: the cached_status is only updated by commands (DR §3.6), so
  // the read GET can return a stale `pending` after the target has acted. The refresh POST re-checks
  // non-terminal rows against the target and persists terminal transitions; we then reload the list. For
  // cross-deployment rows this calls the remote target — if it's unreachable (502) we surface a
  // non-blocking, retryable notice (not the error modal / Sentry); the tab stays usable.
  const runRefresh = useCallback(async () => {
    try {
      setIsRefreshing(true);
      await axios.post(`${baseRoute}/refresh`);
      setRefreshUnreachable(false);
      await reloadAll();
    } catch (error) {
      if (isTargetUnreachableError(error)) {
        setRefreshUnreachable(true);
      } else {
        await reportAxiosError(error);
      }
    } finally {
      setIsRefreshing(false);
    }
  }, [baseRoute, reloadAll]);

  // Guards the on-load reconcile refresh to once per org (declared before the org-switch reset below so
  // that reset can re-arm it).
  const didRefresh = useRef(false);

  // The tab stays mounted across an in-place sysconfig org switch. Clear the stale "couldn't refresh"
  // notice AND re-arm the reconcile refresh so the newly-selected org runs its own refresh — surfacing
  // its own unreachable state — rather than inheriting the previous org's cleared banner. This effect is
  // declared before the refresh effect, so on an org change it re-arms the guard before the guard is read.
  useEffect(() => {
    setRefreshUnreachable(false);
    didRefresh.current = false;
  }, [organization.id]);

  // A command (needs WRITE) — skipped for read-only viewers, who can't act on the rows anyway. Runs once
  // per org (re-armed by the org-switch effect above).
  useEffect(() => {
    if (didRefresh.current || !canWrite) {
      return;
    }
    didRefresh.current = true;
    void runRefresh();
  }, [canWrite, runRefresh]);

  const sourcePairings = useMemo(
    () => (pairings?.items ?? []).filter((p) => p.source_org_external_id === organization.external_id),
    [pairings, organization.external_id]
  );
  const isTarget = useMemo(
    () => (pairings?.items ?? []).some((p) => p.target_org_external_id === organization.external_id),
    [pairings, organization.external_id]
  );

  const outgoingRows = outgoing?.items ?? [];
  const pendingOutgoing = useMemo(
    () => (outgoing?.items ?? []).filter((r) => r.cached_status === PROMOTION_STATUS.PENDING),
    [outgoing]
  );
  const incomingRows = incoming?.items ?? [];
  const showOutgoing = sourcePairings.length > 0 || outgoingRows.length > 0;
  const showIncoming = isTarget || incomingRows.length > 0;
  const pendingIncomingCount = incomingRows.filter((r) => r.status === PROMOTION_STATUS.PENDING).length;
  const hasPendingIncoming = pendingIncomingCount > 0;
  // While the pairings/lists are still resolving we can't yet tell whether the tab is empty — show a
  // skeleton instead of briefly flashing the "No environment pairings" empty state. The sub-tabs wait for
  // the same reason: mounted while one direction is still loading, that direction's tab is disabled and the
  // router rewrites a deep link to it (e.g. .../promotions/outgoing) onto the other tab.
  const isLoading = pairingsLoading || outgoingLoading || incomingLoading;

  // One props object for both panes (InnerTabs is generic over a single tab-props type); each reads its slice.
  const paneProps: PromotionsPaneProps = {
    incoming: {
      rows: incomingRows,
      isError: incomingError,
      onView: setIncomingDetailUuid,
      onRollback: setRollbackRow,
    },
    outgoing: {
      rows: outgoingRows,
      isError: outgoingError,
      onView: setOutgoingDetailUuid,
      canCreate: canWrite && sourcePairings.length > 0,
      onCreate: () => setIsCreateOpen(true),
    },
  };
  // A tab is dropped (not merely emptied) when the org plays no part in that direction — no pairing in that
  // role and no rows — so a source-only or target-only org sees a single tab.
  const tabs = [
    {
      label: hasPendingIncoming ? `Incoming · ${pendingIncomingCount} pending` : 'Incoming',
      url: PROMOTION_TABS.INCOMING,
      component: IncomingPromotionsPane,
      props: paneProps,
      disableTab: !showIncoming,
    },
    {
      label: 'Outgoing',
      url: PROMOTION_TABS.OUTGOING,
      component: OutgoingPromotionsPane,
      props: paneProps,
      disableTab: !showOutgoing,
    },
  ];

  return (
    <CardDialog
      title="Promotions"
      action={
        canWrite && (
          <div className="flex items-center gap-x-8">
            {/* Upload-source promotion is internal (super-org) only, even though the Promotions tab can
                appear externally via the CONFIGURATION_MANAGEMENT flag — mirrors the Snapshots & Backup
                internal-only precedent. It lands in this org's Incoming list (target-local), so it sits by
                the tab header rather than in the Outgoing "+ New promotion" flow (which needs a pairing). */}
            {isInternal && (
              <Button
                variant="text"
                color="primary"
                startIcon={<UploadIcon size={16} iconColor="currentColor" />}
                onClick={() => setIsUploadOpen(true)}
              >
                Promote from file
              </Button>
            )}
            <Button
              variant="text"
              color="primary"
              disabled={isRefreshing}
              startIcon={<NewRefreshIcon iconColor="currentColor" />}
              onClick={() => void runRefresh()}
            >
              {isRefreshing ? 'Refreshing…' : 'Refresh'}
            </Button>
          </div>
        )
      }
    >
      <div className="flex flex-col gap-24 p-20">
        {hasPendingIncoming && (
          <AlertBanner
            withIcon
            alertType={AlertBanner.ALERT_TYPES.WARNING}
            title="Configuration locked"
            note="A promotion request is pending review. Configuration changes should be held until it is approved or rejected."
          />
        )}

        {/* The refresh runs from the header for every writer, so its failure is reported here, above the
            tabs — a viewer on Incoming (or a target-only org with no Outgoing tab) must see it too. */}
        {refreshUnreachable && (
          <AlertBanner
            withIcon
            alertType={AlertBanner.ALERT_TYPES.WARNING}
            title="Couldn’t refresh remote statuses"
            note={
              <div className="flex flex-wrap items-center gap-x-12 gap-y-8">
                <span>{TARGET_UNREACHABLE_MESSAGE}</span>
                <Button variant="outlined" disabled={isRefreshing} onClick={() => void runRefresh()}>
                  {isRefreshing ? 'Retrying…' : 'Retry'}
                </Button>
              </div>
            }
          />
        )}

        {isLoading && <SkeletonTable rowsCount={3} columnsCount={FULL_TAB_SKELETON_COLUMNS} />}

        {!isLoading && !showOutgoing && !showIncoming && (
          <EmptyState
            title="No environment pairings"
            subtitle="Configuration promotion is set up by support. Once a pairing exists, promotions will appear here."
          />
        )}

        {!isLoading && (showOutgoing || showIncoming) && <InnerTabs tabs={tabs} />}
      </div>

      <CreatePromotionDialog
        open={isCreateOpen}
        organizationId={organization.id}
        sourcePairings={sourcePairings}
        pendingOutgoing={pendingOutgoing}
        onClose={() => setIsCreateOpen(false)}
        onCreated={reloadAll}
        onViewPendingPromotion={(requestUuid) => {
          setIsCreateOpen(false);
          setOutgoingDetailUuid(requestUuid);
        }}
      />
      {isInternal && (
        <CreateUploadPromotionDialog
          open={isUploadOpen}
          organizationId={organization.id}
          onClose={() => setIsUploadOpen(false)}
          onCreated={reloadAll}
        />
      )}
      <OutgoingPromotionDetailDialog
        requestUuid={outgoingDetailUuid}
        organizationId={organization.id}
        onClose={() => setOutgoingDetailUuid(null)}
        onChanged={reloadAll}
      />
      <IncomingPromotionDetailDialog
        requestUuid={incomingDetailUuid}
        organizationId={organization.id}
        onClose={() => setIncomingDetailUuid(null)}
        onChanged={reloadAll}
        onApplied={handleApplied}
      />
      <RollbackPromotionDialog
        promotion={rollbackRow}
        organizationId={organization.id}
        onClose={() => setRollbackRow(null)}
        onChanged={reloadAll}
        onApplied={handleApplied}
      />
    </CardDialog>
  );
};

export default PromotionsTab;
